import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import * as LucideIcons from 'lucide-react';
import { Heart, Lock } from 'lucide-react';
import { toast } from 'sonner';
import BreathingExercise from '../components/BreathingExercise';
import { useAccessControl } from '../hooks/useAccessControl';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Outils = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { canAccessTool } = useAccessControl();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBreathing, setShowBreathing] = useState(false);

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      const response = await axios.get(`${API_URL}/tools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTools(response.data);
    } catch (error) {
      console.error('Erreur chargement outils:', error);
      toast.error('Erreur lors du chargement des outils');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700 font-sans">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="outils-page">
      {/* Breathing Exercise Modal */}
      {showBreathing && (
        <BreathingExercise onClose={() => setShowBreathing(false)} />
      )}

      {/* Header */}
      <div className="bg-gradient-to-b from-sage-100 to-sand-50 px-6 py-8">
        <h1 className="text-4xl font-serif font-light text-sage-700 mb-2" data-testid="outils-title">
          Outils
        </h1>
        <p className="text-sage-700/70 font-sans">
          Vos outils pratiques au quotidien
        </p>
      </div>

      {/* Urgence button - Prominent */}
      <div className="px-6 py-4">
        <Button
          onClick={() => setShowBreathing(true)}
          className="w-full h-20 bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white rounded-2xl shadow-lg shadow-rose-500/30 transition-all hover:shadow-xl hover:shadow-rose-500/40"
          data-testid="urgence-button"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" fill="currentColor" />
            </div>
            <div className="text-left">
              <p className="text-lg font-semibold">Urgence</p>
              <p className="text-sm text-white/80">Cohérence cardiaque • 1min30</p>
            </div>
          </div>
        </Button>
      </div>

      {/* Tools by category */}
      <div className="px-6 py-4">
        {/* Catégorie 1: Envie ou pulsion */}
        <p className="text-xs font-semibold text-declic uppercase tracking-wider mb-3">Envie ou pulsion</p>
        <div className="grid grid-cols-2 gap-3 mb-6">
          {tools.filter(t => ['faim-ou-envie', 'que-faire-si', 'envie-maintenant', 'apres-repas-riche'].includes(t.slug)).map((tool) => {
            const IconComponent = LucideIcons[tool.icon] || LucideIcons.Sparkles;
            const locked = !canAccessTool(tool.slug);
            return (
              <Button key={tool.id} variant="outline" onClick={() => locked ? toast.error("Passez à l'accès complet pour débloquer cet outil") : navigate(`/outils/${tool.slug}`)}
                className={`h-auto p-5 flex flex-col items-center justify-center space-y-2 border-2 transition-all ${locked ? 'border-sage-200 opacity-50' : 'border-declic/20 hover:border-declic hover:shadow-md bg-declic/[0.02]'}`}
                data-testid={`tool-card-${tool.slug}`}>
                {locked ? <Lock className="w-8 h-8 text-sage-300" strokeWidth={1.5} /> : <IconComponent className="w-8 h-8 text-declic" strokeWidth={1.5} />}
                <span className="text-sm font-sans font-medium text-sage-700 text-center leading-snug">{tool.title}</span>
              </Button>
            );
          })}
        </div>

        {/* Catégorie 2: Pendant le repas */}
        <p className="text-xs font-semibold text-sage-500 uppercase tracking-wider mb-3">Pendant le repas</p>
        <div className="grid grid-cols-2 gap-3 mb-6">
          {tools.filter(t => ['mange-trop-vite', 'jauge-de-faim'].includes(t.slug)).map((tool) => {
            const IconComponent = LucideIcons[tool.icon] || LucideIcons.Sparkles;
            const locked = !canAccessTool(tool.slug);
            return (
              <Button key={tool.id} variant="outline" onClick={() => locked ? toast.error("Passez à l'accès complet pour débloquer cet outil") : navigate(`/outils/${tool.slug}`)}
                className={`h-auto p-5 flex flex-col items-center justify-center space-y-2 border-2 transition-all ${locked ? 'border-sage-200 opacity-50' : 'border-sage-200 hover:border-sage-500 hover:shadow-md'}`}
                data-testid={`tool-card-${tool.slug}`}>
                {locked ? <Lock className="w-8 h-8 text-sage-300" strokeWidth={1.5} /> : <IconComponent className="w-8 h-8 text-sage-500" strokeWidth={1.5} />}
                <span className="text-sm font-sans font-medium text-sage-700 text-center leading-snug">{tool.title}</span>
              </Button>
            );
          })}
        </div>

        {/* Catégorie 3: Fin de journée & info */}
        <p className="text-xs font-semibold text-sage-500 uppercase tracking-wider mb-3">Fin de journée</p>
        <div className="grid grid-cols-2 gap-3">
          {tools.filter(t => ['checklist-soir', 'le-saviez-vous-'].includes(t.slug)).map((tool) => {
            const IconComponent = LucideIcons[tool.icon] || LucideIcons.Sparkles;
            const locked = !canAccessTool(tool.slug);
            return (
              <Button key={tool.id} variant="outline" onClick={() => locked ? toast.error("Passez à l'accès complet pour débloquer cet outil") : navigate(`/outils/${tool.slug}`)}
                className={`h-auto p-5 flex flex-col items-center justify-center space-y-2 border-2 transition-all ${locked ? 'border-sage-200 opacity-50' : 'border-sand-200 hover:border-sage-500 hover:shadow-md'}`}
                data-testid={`tool-card-${tool.slug}`}>
                {locked ? <Lock className="w-8 h-8 text-sage-300" strokeWidth={1.5} /> : <IconComponent className="w-8 h-8 text-sage-400" strokeWidth={1.5} />}
                <span className="text-sm font-sans font-medium text-sage-700 text-center leading-snug">{tool.title}</span>
              </Button>
            );
          })}
          {/* Outils non catégorisés */}
          {tools.filter(t => !['faim-ou-envie', 'que-faire-si', 'envie-maintenant', 'apres-repas-riche', 'mange-trop-vite', 'jauge-de-faim', 'checklist-soir', 'le-saviez-vous-'].includes(t.slug)).map((tool) => {
            const IconComponent = LucideIcons[tool.icon] || LucideIcons.Sparkles;
            const locked = !canAccessTool(tool.slug);
            return (
              <Button key={tool.id} variant="outline" onClick={() => locked ? toast.error("Passez à l'accès complet pour débloquer cet outil") : navigate(`/outils/${tool.slug}`)}
                className={`h-auto p-5 flex flex-col items-center justify-center space-y-2 border-2 transition-all ${locked ? 'border-sage-200 opacity-50' : 'border-sand-200 hover:border-sage-500 hover:shadow-md'}`}
                data-testid={`tool-card-${tool.slug}`}>
                {locked ? <Lock className="w-8 h-8 text-sage-300" strokeWidth={1.5} /> : <IconComponent className="w-8 h-8 text-sage-500" strokeWidth={1.5} />}
                <span className="text-sm font-sans font-medium text-sage-700 text-center leading-snug">{tool.title}</span>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Tip */}
      <div className="px-6 py-4">
        <div className="bg-sage-50 rounded-xl p-4 border border-sage-100">
          <p className="text-sm text-sage-700 font-sans leading-relaxed">
            <strong>✨ Astuce :</strong> Utilisez ces outils autant de fois que nécessaire. Ils sont conçus pour vous accompagner dans votre quotidien.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Outils;